import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  RefreshCw,
  Eye,
  Trash2,
  AlertCircle,
  Loader2,
  X,
  CheckCircle,
  User,
  Mail as MailIcon,
  Flag,
  AlertTriangle,
  Send,
  ArchiveIcon,
  TrendingUp,
  MessageCircle as MessageCircleIcon,
  Calendar,
  Bell,
  Users,
  Clock,
  BellRing,
  Settings,
  Gift,
  ShoppingBag,
  PlusCircle,
  Shield,
  Smartphone,
  Mail,
  MessageSquare,
  Calendar as CalendarIcon,
  Clock as ClockIcon,
  Link,
  Globe as GlobeIcon,
  AtSign as AtSignIcon,
  PlusCircle as PlusCircleIcon,
  XCircle as XCircleIcon,
  CheckCircle as CheckCircleIcon,
  AlertCircle as AlertCircleIcon,
  HelpCircle as HelpCircleIcon,
  Info as InfoIcon,
  Star as StarIcon,
  Heart as HeartIcon,
  ThumbsUp as ThumbsUpIcon,
  ThumbsDown as ThumbsDownIcon,
  Share2 as Share2Icon,
  Copy as CopyIcon,
  Link as LinkIcon,
  ExternalLink,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Database as DatabaseIcon,
  Server as ServerIcon,
  Shield as ShieldIcon,
  Lock as LockIcon,
  Key as KeyIcon,
  Eye as EyeIcon,
  EyeOff as EyeOffIcon,
  RefreshCw as RefreshCwIcon,
  Save as SaveIcon,
  Folder as FolderIcon,
  File as FileIcon,
  Image as ImageIcon,
  Video as VideoIcon,
  Music as MusicIcon,
  Code as CodeIcon,
  Terminal as TerminalIcon,
  Command as CommandIcon,
  Grid as GridIcon,
  List as ListIcon,
  Layout as LayoutIcon,
  Maximize as MaximizeIcon,
  Minimize as MinimizeIcon,
} from "lucide-react";
import { notificationService } from "../../api/services/notificationService";
import { format, formatDistance } from "date-fns";
import { useToast } from "../../components/common/ThemeToaster";
import { apiClient } from "../../api/axiosInstance";

// Helper Functions
const getNotificationTypeInfo = (type) => {
  const types = {
    info: {
      icon: InfoIcon,
      label: "Info",
      color: "text-blue-500",
      bg: "bg-blue-50 dark:bg-blue-900/20",
      border: "border-blue-200 dark:border-blue-800",
    },
    success: {
      icon: CheckCircleIcon,
      label: "Success",
      color: "text-green-500",
      bg: "bg-green-50 dark:bg-green-900/20",
      border: "border-green-200 dark:border-green-800",
    },
    warning: {
      icon: AlertTriangle,
      label: "Warning",
      color: "text-yellow-500",
      bg: "bg-yellow-50 dark:bg-yellow-900/20",
      border: "border-yellow-200 dark:border-yellow-800",
    },
    error: {
      icon: XCircleIcon,
      label: "Error",
      color: "text-red-500",
      bg: "bg-red-50 dark:bg-red-900/20",
      border: "border-red-200 dark:border-red-800",
    },
    promotion: {
      icon: Gift,
      label: "Promotion",
      color: "text-purple-500",
      bg: "bg-purple-50 dark:bg-purple-900/20",
      border: "border-purple-200 dark:border-purple-800",
    },
    order: {
      icon: ShoppingBag,
      label: "Order",
      color: "text-cyan-500",
      bg: "bg-cyan-50 dark:bg-cyan-900/20",
      border: "border-cyan-200 dark:border-cyan-800",
    },
    account: {
      icon: User,
      label: "Account",
      color: "text-indigo-500",
      bg: "bg-indigo-50 dark:bg-indigo-900/20",
      border: "border-indigo-200 dark:border-indigo-800",
    },
    system: {
      icon: Settings,
      label: "System",
      color: "text-gray-500",
      bg: "bg-gray-100 dark:bg-gray-800",
      border: "border-gray-200 dark:border-gray-700",
    },
  };
  return types[type] || types.info;
};

const getPriorityInfo = (priority) => {
  const priorities = {
    low: {
      icon: Flag,
      label: "Low",
      color: "text-green-500",
      bg: "bg-green-50 dark:bg-green-900/20",
      border: "border-green-200 dark:border-green-800",
    },
    medium: {
      icon: Flag,
      label: "Medium",
      color: "text-blue-500",
      bg: "bg-blue-50 dark:bg-blue-900/20",
      border: "border-blue-200 dark:border-blue-800",
    },
    high: {
      icon: Flag,
      label: "High",
      color: "text-orange-500",
      bg: "bg-orange-50 dark:bg-orange-900/20",
      border: "border-orange-200 dark:border-orange-800",
    },
    urgent: {
      icon: AlertTriangle,
      label: "Urgent",
      color: "text-red-500",
      bg: "bg-red-50 dark:bg-red-900/20",
      border: "border-red-200 dark:border-red-800",
    },
  };
  return priorities[priority] || priorities.medium;
};

const getStatusInfo = (status) => {
  const statuses = {
    unread: {
      icon: Bell,
      label: "Unread",
      color: "text-blue-500",
      bg: "bg-blue-50 dark:bg-blue-900/20",
      border: "border-blue-200 dark:border-blue-800",
    },
    read: {
      icon: CheckCircle,
      label: "Read",
      color: "text-gray-500",
      bg: "bg-gray-100 dark:bg-gray-800",
      border: "border-gray-200 dark:border-gray-700",
    },
    archived: {
      icon: ArchiveIcon,
      label: "Archived",
      color: "text-gray-500",
      bg: "bg-gray-100 dark:bg-gray-800",
      border: "border-gray-200 dark:border-gray-700",
    },
    deleted: {
      icon: Trash2,
      label: "Deleted",
      color: "text-red-500",
      bg: "bg-red-50 dark:bg-red-900/20",
      border: "border-red-200 dark:border-red-800",
    },
  };
  return statuses[status] || statuses.unread;
};

const formatTime = (date) => {
  return formatDistance(new Date(date), new Date(), { addSuffix: true });
};

// Stat Card Component (same styling as MessagesManagement)
const StatCard = ({ icon: Icon, label, value, trend }) => (
  <motion.div
    whileHover={{ y: -2, scale: 1.02 }}
    transition={{ type: "spring", stiffness: 300, damping: 20 }}
    className="relative overflow-hidden bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl border border-white/50 dark:border-gray-800/50 shadow-lg"
  >
    <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-cyan-500 opacity-5" />
    <div className="relative p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 shadow-md">
          <Icon size={20} className="text-white" />
        </div>
        <span className="text-2xl font-bold text-gray-900 dark:text-white">
          {value?.toLocaleString() || 0}
        </span>
      </div>
      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
        {label}
      </p>
      {trend && (
        <div className="flex items-center gap-1 mt-2 text-xs">
          <TrendingUp size={12} className="text-emerald-500" />
          <span className="text-emerald-600 dark:text-emerald-400">
            +{trend}%
          </span>
          <span className="text-gray-500">vs last week</span>
        </div>
      )}
    </div>
    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 to-cyan-500 " />
  </motion.div>
);

// Notification Card Component (matching MessageCard styling)
const NotificationCard = ({
  notification,
  isSelected,
  onSelect,
  onViewDetails,
  onMarkAsRead,
  onArchive,
  onDelete,
}) => {
  const type = getNotificationTypeInfo(notification.type);
  const priority = getPriorityInfo(notification.priority);
  const status = getStatusInfo(notification.status);
  const TypeIcon = type.icon;
  const PriorityIcon = priority.icon;
  const StatusIcon = status.icon;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      whileHover={{ y: -2 }}
      transition={{ type: "spring", stiffness: 300 }}
      className={`group bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm rounded-2xl border ${
        notification.status === "unread"
          ? "border-primary-200 dark:border-primary-800 bg-gradient-to-r from-primary-50/30 to-white dark:from-primary-900/10 dark:to-gray-900"
          : "border-gray-200/50 dark:border-gray-800/50"
      } shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden`}
    >
      <div className="p-5">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 pt-1">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onSelect(notification.id)}
              className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${status.bg} ${status.color} border ${status.border}`}
                  >
                    <StatusIcon size={10} /> {status.label}
                  </span>
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${priority.bg} ${priority.color} border ${priority.border}`}
                  >
                    <PriorityIcon size={10} /> {priority.label} Priority
                  </span>
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${type.bg} ${type.color} border ${type.border}`}
                  >
                    <TypeIcon size={10} /> {type.label}
                  </span>
                </div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">
                  {notification.title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-2">
                  {notification.message}
                </p>
                <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                  {notification.userId && (
                    <span className="flex items-center gap-1">
                      <User size={12} /> User ID: {notification.userId}
                    </span>
                  )}
                  {notification.adminId && (
                    <span className="flex items-center gap-1">
                      <Shield size={12} /> Admin ID: {notification.adminId}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Calendar size={12} /> {formatTime(notification.createdAt)}
                  </span>
                  {notification.expiresAt && (
                    <span className="flex items-center gap-1">
                      <Clock size={12} /> Expires:{" "}
                      {formatTime(notification.expiresAt)}
                    </span>
                  )}
                </div>
                {notification.link && (
                  <div className="flex items-center gap-1 mt-2 text-xs text-primary-500">
                    <Link size={12} /> <span>Has deep link</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => onViewDetails(notification)}
                  className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  title="View Details"
                >
                  <Eye size={14} className="text-blue-500" />
                </button>
                <button
                  onClick={() =>
                    onMarkAsRead(
                      notification.id,
                      notification.status === "read",
                    )
                  }
                  className={`p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ${
                    notification.status === "read"
                      ? "text-green-600"
                      : "text-gray-400"
                  }`}
                  title={
                    notification.status === "read"
                      ? "Mark as Unread"
                      : "Mark as Read"
                  }
                >
                  <MailIcon size={14} />
                </button>
                <button
                  onClick={() => onArchive(notification.id)}
                  className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  title="Archive"
                >
                  <ArchiveIcon size={14} className="text-gray-500" />
                </button>
                <button
                  onClick={() => onDelete(notification.id)}
                  className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  title="Delete"
                >
                  <Trash2 size={14} className="text-red-500" />
                </button>
              </div>
            </div>
            {notification.isPushSent && (
              <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
                <div className="flex items-center gap-2 text-xs">
                  <Smartphone size={12} className="text-blue-500" />{" "}
                  <span className="text-gray-500">Push notification sent</span>
                  {notification.isEmailSent && (
                    <>
                      <span className="text-gray-400">•</span>
                      <Mail size={12} className="text-green-500" />{" "}
                      <span className="text-gray-500">Email sent</span>
                    </>
                  )}
                  {notification.isSmsSent && (
                    <>
                      <span className="text-gray-400">•</span>
                      <MessageSquare
                        size={12}
                        className="text-purple-500"
                      />{" "}
                      <span className="text-gray-500">SMS sent</span>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Details Modal Component (matching MessagesManagement styling)
const DetailsModal = ({ notification, onClose, onMarkAsRead }) => {
  const type = getNotificationTypeInfo(notification.type);
  const priority = getPriorityInfo(notification.priority);
  const status = getStatusInfo(notification.status);
  const TypeIcon = type.icon;
  const PriorityIcon = priority.icon;
  const StatusIcon = status.icon;

  // Parse JSON data if exists
  const notificationData = notification.getData
    ? notification.getData()
    : notification.data
      ? JSON.parse(notification.data)
      : null;
  const notificationActions = notification.getActions
    ? notification.getActions()
    : notification.actions
      ? JSON.parse(notification.actions)
      : null;
  const notificationMetadata = notification.getMetadata
    ? notification.getMetadata()
    : notification.metadata
      ? JSON.parse(notification.metadata)
      : null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white dark:bg-gray-900 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 p-5 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Notification Details
            </h2>
            <p className="text-sm font-mono text-primary-600 dark:text-primary-400 mt-0.5">
              {notification.uuid}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <X size={20} />
          </button>
        </div>
        <div className="p-6 space-y-6">
          <div className="flex flex-wrap gap-3">
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium rounded-full ${status.bg} ${status.color} border ${status.border}`}
            >
              <StatusIcon size={14} /> {status.label}
            </span>
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium rounded-full ${priority.bg} ${priority.color} border ${priority.border}`}
            >
              <PriorityIcon size={14} /> {priority.label} Priority
            </span>
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium rounded-full ${type.bg} ${type.color} border ${type.border}`}
            >
              <TypeIcon size={14} /> {type.label}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50/50 dark:bg-gray-800/50 rounded-xl">
            <div>
              <p className="text-xs text-gray-500">Title</p>
              <p className="text-sm font-medium">{notification.title}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Recipient Type</p>
              <p className="text-sm">
                {notification.userId
                  ? "User"
                  : notification.adminId
                    ? "Admin"
                    : "System"}
              </p>
            </div>
            {notification.userId && (
              <div>
                <p className="text-xs text-gray-500">User ID</p>
                <p className="text-sm">{notification.userId}</p>
              </div>
            )}
            {notification.adminId && (
              <div>
                <p className="text-xs text-gray-500">Admin ID</p>
                <p className="text-sm">{notification.adminId}</p>
              </div>
            )}
            <div>
              <p className="text-xs text-gray-500">Created</p>
              <p className="text-sm">
                {format(new Date(notification.createdAt), "MMM d, yyyy HH:mm")}
              </p>
            </div>
            {notification.readAt && (
              <div>
                <p className="text-xs text-gray-500">Read</p>
                <p className="text-sm">
                  {format(new Date(notification.readAt), "MMM d, yyyy HH:mm")}
                </p>
              </div>
            )}
            {notification.expiresAt && (
              <div>
                <p className="text-xs text-gray-500">Expires</p>
                <p className="text-sm">
                  {format(
                    new Date(notification.expiresAt),
                    "MMM d, yyyy HH:mm",
                  )}
                </p>
              </div>
            )}
          </div>

          <div>
            <h3 className="text-sm font-semibold mb-2">Message</h3>
            <div className="bg-gray-50/50 dark:bg-gray-800/50 rounded-xl p-4 whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">
              {notification.message}
            </div>
          </div>

          {notification.link && (
            <div>
              <h3 className="text-sm font-semibold mb-2">Deep Link</h3>
              <div className="flex items-center gap-2 p-3 bg-gray-50/50 dark:bg-gray-800/50 rounded-xl">
                <Link size={14} className="text-primary-500" />
                <a
                  href={notification.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                >
                  {notification.link}
                </a>
              </div>
            </div>
          )}

          {notificationData && (
            <div>
              <h3 className="text-sm font-semibold mb-2">Additional Data</h3>
              <pre className="bg-gray-50/50 dark:bg-gray-800/50 rounded-xl p-4 text-xs overflow-x-auto">
                {JSON.stringify(notificationData, null, 2)}
              </pre>
            </div>
          )}

          {notificationActions && notificationActions.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold mb-2">Actions</h3>
              <div className="flex flex-wrap gap-2">
                {notificationActions.map((action, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg"
                  >
                    <span className="text-sm">{action.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-100 dark:border-gray-800 text-xs text-gray-500">
            <div>
              <span className="block">Push Sent</span>
              <span className="text-gray-900 dark:text-white">
                {notification.isPushSent ? "Yes" : "No"}
              </span>
            </div>
            <div>
              <span className="block">Email Sent</span>
              <span className="text-gray-900 dark:text-white">
                {notification.isEmailSent ? "Yes" : "No"}
              </span>
            </div>
            <div>
              <span className="block">SMS Sent</span>
              <span className="text-gray-900 dark:text-white">
                {notification.isSmsSent ? "Yes" : "No"}
              </span>
            </div>
          </div>

          {notificationMetadata && (
            <div>
              <h3 className="text-sm font-semibold mb-2">Metadata</h3>
              <pre className="bg-gray-50/50 dark:bg-gray-800/50 rounded-xl p-4 text-xs overflow-x-auto">
                {JSON.stringify(notificationMetadata, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// Create Notification Modal
const CreateNotificationModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: "",
    message: "",
    type: "info",
    priority: "medium",
    recipientType: "user",
    userId: "",
    adminId: "",
    link: "",
    expiresAt: "",
    sendPush: false,
    sendEmail: false,
    sendSms: false,
    data: "",
    actions: "",
    metadata: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [users, setUsers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showAdminDropdown, setShowAdminDropdown] = useState(false);
  const { success: toastSuccess, error: toastError } = useToast();
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedAdmin, setSelectedAdmin] = useState(null);

  // Fetch users when recipient type is user
  useEffect(() => {
    if (formData.recipientType === "user") {
      fetchUsers();
    }
  }, [formData.recipientType, userSearchTerm]);

  // Fetch admins when recipient type is admin
  useEffect(() => {
    if (formData.recipientType === "admin") {
      fetchAdmins();
    }
  }, [formData.recipientType]);

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await apiClient.get("/users", {
        params: {
          limit: 50,
          search: userSearchTerm || undefined,
          isActive: true,
        },
      });
      let usersData = [];
      if (response?.data?.data?.users) {
        usersData = response.data.data.users;
      } else if (response?.data?.users) {
        usersData = response.data.users;
      } else if (response?.users) {
        usersData = response.users;
      }
      setUsers(usersData);
    } catch (err) {
      console.error("Error fetching users:", err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchAdmins = async () => {
    setLoadingAdmins(true);
    try {
      const response = await apiClient.get("/admins", {
        params: {
          limit: 50,
          isActive: true,
        },
      });
      let adminsData = [];
      if (response?.data?.data?.admins) {
        adminsData = response.data.data.admins;
      } else if (response?.data?.admins) {
        adminsData = response.data.admins;
      } else if (response?.admins) {
        adminsData = response.admins;
      }
      setAdmins(adminsData);
    } catch (err) {
      console.error("Error fetching admins:", err);
    } finally {
      setLoadingAdmins(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear selected user/admin when recipient type changes
    if (name === "recipientType") {
      setSelectedUser(null);
      setSelectedAdmin(null);
      setFormData((prev) => ({
        ...prev,
        userId: "",
        adminId: "",
      }));
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setFormData((prev) => ({
      ...prev,
      userId: user.id.toString(),
    }));
    setShowUserDropdown(false);
    setUserSearchTerm("");
  };

  const handleSelectAdmin = (admin) => {
    setSelectedAdmin(admin);
    setFormData((prev) => ({
      ...prev,
      adminId: admin.id.toString(),
    }));
    setShowAdminDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      setError("Title is required");
      toastError("Title is required");
      return;
    }
    if (!formData.message.trim()) {
      setError("Message is required");
      toastError("Message is required");
      return;
    }

    try {
      setLoading(true);
      const notificationData = {
        title: formData.title,
        message: formData.message,
        type: formData.type,
        priority: formData.priority,
        link: formData.link || undefined,
        expiresAt: formData.expiresAt || undefined,
        isPushSent: formData.sendPush,
        isEmailSent: formData.sendEmail,
        isSmsSent: formData.sendSms,
      };

      // Add data if provided
      if (formData.data) {
        try {
          notificationData.data = JSON.parse(formData.data);
        } catch (e) {
          notificationData.data = formData.data;
        }
      }

      // Add actions if provided
      if (formData.actions) {
        try {
          notificationData.actions = JSON.parse(formData.actions);
        } catch (e) {
          notificationData.actions = formData.actions;
        }
      }

      // Add metadata if provided
      if (formData.metadata) {
        try {
          notificationData.metadata = JSON.parse(formData.metadata);
        } catch (e) {
          notificationData.metadata = formData.metadata;
        }
      }

      let response;
      if (formData.recipientType === "user" && formData.userId) {
        response = await notificationService.createUserNotification(
          parseInt(formData.userId),
          notificationData,
        );
      } else if (formData.recipientType === "admin" && formData.adminId) {
        response = await notificationService.createAdminNotification(
          parseInt(formData.adminId),
          notificationData,
        );
      } else if (formData.recipientType === "all") {
        response =
          await notificationService.createAllUsersNotification(
            notificationData,
          );
      } else {
        setError("Please select a valid recipient");
        toastError("Please select a valid recipient");
        return;
      }

      toastSuccess("Notification sent successfully");
      onSuccess();
    } catch (err) {
      toastError(err.message || "Failed to send notification");
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getUserDisplayName = (user) => {
    if (!user) return "";
    const name = `${user.firstName || ""} ${user.lastName || ""}`.trim();
    return name || user.email || `User #${user.id}`;
  };

  const getAdminDisplayName = (admin) => {
    if (!admin) return "";
    const name = `${admin.firstName || ""} ${admin.lastName || ""}`.trim();
    return name || admin.email || `Admin #${admin.id}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white dark:bg-gray-900 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 p-5 flex items-center justify-between">
          <h2 className="text-xl font-bold">Send Custom Notification</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-xl flex items-center gap-2 text-red-600 text-sm">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium mb-2">Title *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border focus:ring-2 focus:ring-primary-500"
              placeholder="Enter notification title"
              required
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium mb-2">Message *</label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={4}
              className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border focus:ring-2 focus:ring-primary-500"
              placeholder="Enter notification message"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Type */}
            <div>
              <label className="block text-sm font-medium mb-2">Type</label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border"
              >
                <option value="info">Info</option>
                <option value="success">Success</option>
                <option value="warning">Warning</option>
                <option value="error">Error</option>
                <option value="promotion">Promotion</option>
                <option value="order">Order</option>
                <option value="account">Account</option>
                <option value="system">System</option>
              </select>
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-medium mb-2">Priority</label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Recipient Type */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Recipient Type
              </label>
              <select
                name="recipientType"
                value={formData.recipientType}
                onChange={handleChange}
                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border"
              >
                <option value="user">Specific User</option>
                <option value="admin">Specific Admin</option>
                <option value="all">All Users</option>
              </select>
            </div>

            {/* User Selection (conditional) */}
            {formData.recipientType === "user" && (
              <div>
                <label className="block text-sm font-medium mb-2">
                  Select User *
                </label>
                <div className="relative">
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={
                        selectedUser
                          ? getUserDisplayName(selectedUser)
                          : userSearchTerm
                      }
                      onChange={(e) => {
                        setUserSearchTerm(e.target.value);
                        setSelectedUser(null);
                        setFormData((prev) => ({ ...prev, userId: "" }));
                        setShowUserDropdown(true);
                      }}
                      onFocus={() => setShowUserDropdown(true)}
                      placeholder="Search by name or email..."
                      className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border focus:ring-2 focus:ring-primary-500"
                    />
                    {loadingUsers && (
                      <Loader2
                        size={16}
                        className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400"
                      />
                    )}
                  </div>

                  {/* User Dropdown */}
                  {showUserDropdown && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setShowUserDropdown(false)}
                      />
                      <div className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                        {loadingUsers ? (
                          <div className="p-4 text-center text-gray-500">
                            <Loader2
                              size={20}
                              className="animate-spin mx-auto"
                            />
                            <p className="text-sm mt-2">Loading users...</p>
                          </div>
                        ) : users.length === 0 ? (
                          <div className="p-4 text-center text-gray-500">
                            <p className="text-sm">No users found</p>
                          </div>
                        ) : (
                          users.map((user) => (
                            <button
                              key={user.id}
                              type="button"
                              onClick={() => handleSelectUser(user)}
                              className="w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-3"
                            >
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-white text-sm font-bold">
                                {user.firstName?.charAt(0) ||
                                  user.email?.charAt(0) ||
                                  "U"}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                  {getUserDisplayName(user)}
                                </p>
                                <p className="text-xs text-gray-500 truncate">
                                  {user.email} • ID: {user.id}
                                </p>
                              </div>
                              {user.company && (
                                <span className="text-xs text-gray-400">
                                  {user.company}
                                </span>
                              )}
                            </button>
                          ))
                        )}
                      </div>
                    </>
                  )}
                </div>
                {selectedUser && (
                  <p className="text-xs text-green-600 mt-1">
                    ✓ Selected: {getUserDisplayName(selectedUser)} (ID:{" "}
                    {selectedUser.id})
                  </p>
                )}
              </div>
            )}

            {/* Admin Selection (conditional) */}
            {formData.recipientType === "admin" && (
              <div>
                <label className="block text-sm font-medium mb-2">
                  Select Admin *
                </label>
                <div className="relative">
                  <div className="relative">
                    <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={
                        selectedAdmin ? getAdminDisplayName(selectedAdmin) : ""
                      }
                      onFocus={() => setShowAdminDropdown(true)}
                      placeholder="Click to select admin..."
                      readOnly
                      className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border focus:ring-2 focus:ring-primary-500 cursor-pointer"
                    />
                  </div>

                  {/* Admin Dropdown */}
                  {showAdminDropdown && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setShowAdminDropdown(false)}
                      />
                      <div className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                        {loadingAdmins ? (
                          <div className="p-4 text-center text-gray-500">
                            <Loader2
                              size={20}
                              className="animate-spin mx-auto"
                            />
                            <p className="text-sm mt-2">Loading admins...</p>
                          </div>
                        ) : admins.length === 0 ? (
                          <div className="p-4 text-center text-gray-500">
                            <p className="text-sm">No admins found</p>
                          </div>
                        ) : (
                          admins.map((admin) => (
                            <button
                              key={admin.id}
                              type="button"
                              onClick={() => handleSelectAdmin(admin)}
                              className="w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-3"
                            >
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-sm font-bold">
                                {admin.firstName?.charAt(0) ||
                                  admin.email?.charAt(0) ||
                                  "A"}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                  {getAdminDisplayName(admin)}
                                </p>
                                <p className="text-xs text-gray-500 truncate">
                                  {admin.email} •{" "}
                                  {admin.role?.replace("_", " ")} • ID:{" "}
                                  {admin.id}
                                </p>
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    </>
                  )}
                </div>
                {selectedAdmin && (
                  <p className="text-xs text-green-600 mt-1">
                    ✓ Selected: {getAdminDisplayName(selectedAdmin)} (ID:{" "}
                    {selectedAdmin.id})
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Link */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Deep Link (optional)
              </label>
              <input
                type="url"
                name="link"
                value={formData.link}
                onChange={handleChange}
                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border"
                placeholder="https://..."
              />
            </div>

            {/* Expiry Date */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Expires At (optional)
              </label>
              <input
                type="datetime-local"
                name="expiresAt"
                value={formData.expiresAt}
                onChange={handleChange}
                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border"
              />
            </div>
          </div>

          {/* Delivery Methods */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Delivery Methods
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="sendPush"
                  checked={formData.sendPush}
                  onChange={handleChange}
                  className="rounded border-gray-300"
                />
                <span className="text-sm">Push Notification</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="sendEmail"
                  checked={formData.sendEmail}
                  onChange={handleChange}
                  className="rounded border-gray-300"
                />
                <span className="text-sm">Email</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="sendSms"
                  checked={formData.sendSms}
                  onChange={handleChange}
                  className="rounded border-gray-300"
                />
                <span className="text-sm">SMS</span>
              </label>
            </div>
          </div>

          {/* JSON Data */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Additional Data (JSON format - optional)
            </label>
            <textarea
              name="data"
              value={formData.data}
              onChange={handleChange}
              rows={3}
              className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border font-mono text-sm"
              placeholder='{"key": "value"}'
            />
          </div>

          {/* Actions */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Actions (JSON array - optional)
            </label>
            <textarea
              name="actions"
              value={formData.actions}
              onChange={handleChange}
              rows={3}
              className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border font-mono text-sm"
              placeholder='[{"label": "View", "action": "view"}, {"label": "Dismiss", "action": "dismiss"}]'
            />
          </div>

          {/* Metadata */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Metadata (JSON format - optional)
            </label>
            <textarea
              name="metadata"
              value={formData.metadata}
              onChange={handleChange}
              rows={3}
              className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border font-mono text-sm"
              placeholder='{"source": "admin_panel", "campaign": "summer_sale"}'
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 border rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-2.5 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl font-medium disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Send size={18} />
              )}
              {loading ? "Sending..." : "Send Notification"}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Bulk Send Modal
const BulkSendModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: "",
    message: "",
    type: "info",
    priority: "medium",
    userIds: "",
    link: "",
    expiresAt: "",
    sendPush: false,
    sendEmail: false,
    sendSms: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const { success: toastSuccess, error: toastError } = useToast();

  useEffect(() => {
    fetchUsers();
  }, [userSearchTerm]);

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await apiClient.get("/users", {
        params: {
          limit: 50,
          search: userSearchTerm || undefined,
          isActive: true,
        },
      });
      let usersData = [];
      if (response?.data?.data?.users) {
        usersData = response.data.data.users;
      } else if (response?.data?.users) {
        usersData = response.data.users;
      } else if (response?.users) {
        usersData = response.users;
      }
      setUsers(usersData);
    } catch (err) {
      console.error("Error fetching users:", err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSelectUser = (user) => {
    if (!selectedUsers.find((u) => u.id === user.id)) {
      const newSelectedUsers = [...selectedUsers, user];
      setSelectedUsers(newSelectedUsers);
      setFormData((prev) => ({
        ...prev,
        userIds: newSelectedUsers.map((u) => u.id).join(","),
      }));
    }
    setUserSearchTerm("");
    setShowUserDropdown(false);
  };

  const handleRemoveUser = (userId) => {
    const newSelectedUsers = selectedUsers.filter((u) => u.id !== userId);
    setSelectedUsers(newSelectedUsers);
    setFormData((prev) => ({
      ...prev,
      userIds: newSelectedUsers.map((u) => u.id).join(","),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      setError("Title is required");
      toastError("Title is required");
      return;
    }
    if (!formData.message.trim()) {
      setError("Message is required");
      toastError("Message is required");
      return;
    }

    const userIdsArray = selectedUsers.map((u) => u.id);

    if (userIdsArray.length === 0) {
      setError("At least one user is required");
      toastError("At least one user is required");
      return;
    }

    try {
      setLoading(true);
      await notificationService.createBulkUserNotifications({
        userIds: userIdsArray,
        title: formData.title,
        message: formData.message,
        type: formData.type,
        priority: formData.priority,
        link: formData.link || undefined,
        expiresAt: formData.expiresAt || undefined,
        isPushSent: formData.sendPush,
        isEmailSent: formData.sendEmail,
        isSmsSent: formData.sendSms,
      });

      toastSuccess(`Notification sent to ${userIdsArray.length} user(s)`);
      onSuccess();
    } catch (err) {
      toastError(err.message || "Failed to send notifications");
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getUserDisplayName = (user) => {
    const name = `${user.firstName || ""} ${user.lastName || ""}`.trim();
    return name || user.email || `User #${user.id}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        className="bg-white dark:bg-gray-900 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 p-5 flex items-center justify-between">
          <h2 className="text-xl font-bold">Bulk Send Notifications</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-xl flex items-center gap-2 text-red-600 text-sm">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {/* User Selection */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Select Users *
            </label>
            <div className="relative">
              <div className="relative">
                <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={userSearchTerm}
                  onChange={(e) => {
                    setUserSearchTerm(e.target.value);
                    setShowUserDropdown(true);
                  }}
                  onFocus={() => setShowUserDropdown(true)}
                  placeholder="Search users by name or email..."
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border focus:ring-2 focus:ring-primary-500"
                />
                {loadingUsers && (
                  <Loader2
                    size={16}
                    className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400"
                  />
                )}
              </div>

              {/* User Dropdown */}
              {showUserDropdown && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowUserDropdown(false)}
                  />
                  <div className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                    {loadingUsers ? (
                      <div className="p-4 text-center text-gray-500">
                        <Loader2 size={20} className="animate-spin mx-auto" />
                        <p className="text-sm mt-2">Loading users...</p>
                      </div>
                    ) : users.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">
                        <p className="text-sm">No users found</p>
                      </div>
                    ) : (
                      users.map((user) => (
                        <button
                          key={user.id}
                          type="button"
                          onClick={() => handleSelectUser(user)}
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-3"
                        >
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-white text-sm font-bold">
                            {user.firstName?.charAt(0) ||
                              user.email?.charAt(0) ||
                              "U"}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {getUserDisplayName(user)}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {user.email} • ID: {user.id}
                            </p>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Selected Users Tags */}
            {selectedUsers.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {selectedUsers.map((user) => (
                  <div
                    key={user.id}
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-sm"
                  >
                    <span>{getUserDisplayName(user)}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveUser(user.id)}
                      className="hover:bg-primary-200 dark:hover:bg-primary-800 rounded-full p-0.5"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-gray-500 mt-2">
              {selectedUsers.length} user(s) selected
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Title *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border"
              placeholder="Enter notification title"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Message *</label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={4}
              className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border"
              placeholder="Enter notification message"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Type</label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border"
              >
                <option value="info">Info</option>
                <option value="success">Success</option>
                <option value="warning">Warning</option>
                <option value="error">Error</option>
                <option value="promotion">Promotion</option>
                <option value="order">Order</option>
                <option value="account">Account</option>
                <option value="system">System</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Priority</label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Deep Link (optional)
            </label>
            <input
              type="url"
              name="link"
              value={formData.link}
              onChange={handleChange}
              className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border"
              placeholder="https://..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Expires At (optional)
            </label>
            <input
              type="datetime-local"
              name="expiresAt"
              value={formData.expiresAt}
              onChange={handleChange}
              className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border"
            />
          </div>

          {/* Delivery Methods */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Delivery Methods
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="sendPush"
                  checked={formData.sendPush}
                  onChange={handleChange}
                  className="rounded border-gray-300"
                />
                <span className="text-sm">Push Notification</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="sendEmail"
                  checked={formData.sendEmail}
                  onChange={handleChange}
                  className="rounded border-gray-300"
                />
                <span className="text-sm">Email</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="sendSms"
                  checked={formData.sendSms}
                  onChange={handleChange}
                  className="rounded border-gray-300"
                />
                <span className="text-sm">SMS</span>
              </label>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 border rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-2.5 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl font-medium disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Send size={18} />
              )}
              {loading
                ? "Sending..."
                : `Send to ${selectedUsers.length} User(s)`}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Main Component
const NotificationsManagement = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [recipientFilter, setRecipientFilter] = useState("all");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [stats, setStats] = useState(null);
  const [selectedNotifications, setSelectedNotifications] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalNotifications, setTotalNotifications] = useState(0);
  const { success: toastSuccess, error: toastError } = useToast();

  // Helper function to fetch all pages
  const fetchAllPages = async (fetchFunction, params) => {
    let allItems = [];
    let currentPage = 1;
    let hasMore = true;

    while (hasMore) {
      try {
        const response = await fetchFunction({
          ...params,
          page: currentPage,
          limit: 100, // Max limit the API accepts
        });

        let items = [];
        if (response?.data?.data?.notifications) {
          items = response.data.data.notifications;
        } else if (response?.data?.notifications) {
          items = response.data.notifications;
        } else if (response?.notifications) {
          items = response.notifications;
        } else if (Array.isArray(response?.data)) {
          items = response.data;
        } else if (Array.isArray(response)) {
          items = response;
        }

        const total =
          response?.data?.pagination?.total ||
          response?.pagination?.total ||
          items.length;

        allItems.push(...items);

        // Check if we've fetched all pages
        hasMore = currentPage * 100 < total;
        currentPage++;
      } catch (error) {
        console.error("Error fetching page:", error);
        hasMore = false;
      }
    }

    return allItems;
  };

  // Updated fetchAllNotifications function
  const fetchAllNotifications = async () => {
    try {
      setLoading(true);

      // Fetch all user notifications across all pages
      const userNotifs = await fetchAllPages(
        notificationService.getUserNotifications.bind(notificationService),
        {
          sortBy,
          sortOrder,
          ...(typeFilter !== "all" && { type: typeFilter }),
          ...(priorityFilter !== "all" && { priority: priorityFilter }),
          ...(statusFilter !== "all" && { status: statusFilter }),
        },
      );

      // Fetch all admin notifications across all pages
      const adminNotifs = await fetchAllPages(
        notificationService.getAdminNotifications.bind(notificationService),
        {
          sortBy,
          sortOrder,
          ...(typeFilter !== "all" && { type: typeFilter }),
          ...(priorityFilter !== "all" && { priority: priorityFilter }),
          ...(statusFilter !== "all" && { status: statusFilter }),
        },
      );

      let allNotifications = [];

      // Add user notifications
      allNotifications.push(
        ...userNotifs.map((n) => ({ ...n, recipientType: "user" })),
      );

      // Add admin notifications
      allNotifications.push(
        ...adminNotifs.map((n) => ({ ...n, recipientType: "admin" })),
      );

      console.log("Total user notifications:", userNotifs.length);
      console.log("Total admin notifications:", adminNotifs.length);
      console.log("Total all notifications:", allNotifications.length);

      // Apply client-side search filter
      let filteredNotifications = [...allNotifications];

      if (searchTerm && searchTerm.trim()) {
        const searchLower = searchTerm.toLowerCase().trim();
        filteredNotifications = filteredNotifications.filter(
          (notification) =>
            notification.title?.toLowerCase().includes(searchLower) ||
            notification.message?.toLowerCase().includes(searchLower),
        );
        console.log("After search filter:", filteredNotifications.length);
      }

      // Apply recipient filter
      if (recipientFilter !== "all") {
        filteredNotifications = filteredNotifications.filter(
          (n) => n.recipientType === recipientFilter,
        );
        console.log("After recipient filter:", filteredNotifications.length);
      }

      // Sort notifications
      filteredNotifications.sort((a, b) => {
        const aValue = a[sortBy];
        const bValue = b[sortBy];
        if (sortOrder === "DESC") {
          return new Date(bValue) - new Date(aValue);
        } else {
          return new Date(aValue) - new Date(bValue);
        }
      });

      // Apply pagination manually
      const startIndex = (currentPage - 1) * 20;
      const endIndex = startIndex + 20;
      const paginatedNotifications = filteredNotifications.slice(
        startIndex,
        endIndex,
      );

      setNotifications(paginatedNotifications);
      setTotalNotifications(filteredNotifications.length);
      setTotalPages(Math.ceil(filteredNotifications.length / 20));

      // Calculate stats from filtered notifications
      calculateStatsFromNotifications(filteredNotifications);
    } catch (err) {
      console.error("Fetch notifications error:", err);
      toastError(err.message || "Failed to load notifications");
      setNotifications([]);
      setTotalNotifications(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  // Calculate stats from notifications array
  const calculateStatsFromNotifications = (
    notificationsList = notifications,
  ) => {
    const calculatedStats = {
      total: notificationsList.length,
      unread: notificationsList.filter(
        (n) => n.status === "unread" || n.status === "new",
      ).length,
      read: notificationsList.filter((n) => n.status === "read").length,
      archived: notificationsList.filter((n) => n.status === "archived").length,
      urgent: notificationsList.filter((n) => n.priority === "urgent").length,
      byType: {},
    };

    // Calculate by type
    notificationsList.forEach((notification) => {
      const type = notification.type;
      if (!calculatedStats.byType[type]) {
        calculatedStats.byType[type] = 0;
      }
      calculatedStats.byType[type]++;
    });

    setStats(calculatedStats);
  };

  // Fetch stats - skip the broken endpoint and calculate from notifications
  const fetchStats = async () => {
    // Don't call the broken stats endpoint, just calculate from existing notifications
    if (notifications.length > 0) {
      calculateStatsFromNotifications(notifications);
    } else {
      // Set default stats
      setStats({
        total: 0,
        unread: 0,
        read: 0,
        archived: 0,
        urgent: 0,
        byType: {},
      });
    }
  };

  useEffect(() => {
    fetchAllNotifications();
  }, [
    typeFilter,
    priorityFilter,
    statusFilter,
    recipientFilter,
    sortBy,
    sortOrder,
    currentPage,
    searchTerm,
  ]);

  // Update stats when notifications change
  useEffect(() => {
    if (notifications.length > 0) {
      calculateStatsFromNotifications(notifications);
    }
  }, [notifications]);

  const handleMarkAsRead = async (id, isRead) => {
    try {
      // Find which type of notification it is
      const notification = notifications.find((n) => n.id === id);
      if (notification?.recipientType === "user") {
        if (!isRead) await notificationService.markAsRead(id);
      } else {
        if (!isRead) await notificationService.markAdminAsRead(id);
      }
      fetchAllNotifications();
      toastSuccess(isRead ? "Marked as unread" : "Marked as read");
    } catch (err) {
      toastError("Failed to update status");
    }
  };

  const handleArchive = async (id) => {
    try {
      const notification = notifications.find((n) => n.id === id);
      if (notification?.recipientType === "user") {
        await notificationService.markAsArchived(id);
      } else {
        await notificationService.markAdminAsArchived(id);
      }
      toastSuccess("Notification archived");
      fetchAllNotifications();
    } catch (err) {
      toastError("Failed to archive");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this notification?")) return;
    try {
      const notification = notifications.find((n) => n.id === id);
      if (notification?.recipientType === "user") {
        await notificationService.deleteNotification(id);
      } else {
        await notificationService.deleteAdminNotification(id);
      }
      toastSuccess("Notification deleted");
      fetchAllNotifications();
      setSelectedNotifications((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    } catch (err) {
      toastError("Failed to delete");
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedNotifications.size) return;
    if (
      !window.confirm(`Delete ${selectedNotifications.size} notification(s)?`)
    )
      return;
    try {
      // Separate user and admin notifications
      const selectedNotifs = notifications.filter((n) =>
        selectedNotifications.has(n.id),
      );
      const userNotifIds = selectedNotifs
        .filter((n) => n.recipientType === "user")
        .map((n) => n.id);
      const adminNotifIds = selectedNotifs
        .filter((n) => n.recipientType === "admin")
        .map((n) => n.id);

      if (userNotifIds.length > 0) {
        await notificationService.bulkDeleteNotifications(userNotifIds);
      }
      if (adminNotifIds.length > 0) {
        await Promise.all(
          adminNotifIds.map((id) =>
            notificationService.deleteAdminNotification(id),
          ),
        );
      }

      toastSuccess(`${selectedNotifications.size} notification(s) deleted`);
      setSelectedNotifications(new Set());
      setSelectAll(false);
      fetchAllNotifications();
    } catch (err) {
      toastError("Failed to delete notifications");
    }
  };

  const handleBulkArchive = async () => {
    if (!selectedNotifications.size) return;
    try {
      const selectedNotifs = notifications.filter((n) =>
        selectedNotifications.has(n.id),
      );
      const userNotifIds = selectedNotifs
        .filter((n) => n.recipientType === "user")
        .map((n) => n.id);
      const adminNotifIds = selectedNotifs
        .filter((n) => n.recipientType === "admin")
        .map((n) => n.id);

      if (userNotifIds.length > 0) {
        await notificationService.archiveMultiple(userNotifIds);
      }
      if (adminNotifIds.length > 0) {
        await Promise.all(
          adminNotifIds.map((id) =>
            notificationService.markAdminAsArchived(id),
          ),
        );
      }

      toastSuccess(`${selectedNotifications.size} notification(s) archived`);
      setSelectedNotifications(new Set());
      setSelectAll(false);
      fetchAllNotifications();
    } catch (err) {
      toastError("Failed to archive notifications");
    }
  };

  const handleBulkRead = async (read) => {
    if (!selectedNotifications.size) return;
    try {
      const selectedNotifs = notifications.filter((n) =>
        selectedNotifications.has(n.id),
      );
      const userNotifIds = selectedNotifs
        .filter((n) => n.recipientType === "user")
        .map((n) => n.id);
      const adminNotifIds = selectedNotifs
        .filter((n) => n.recipientType === "admin")
        .map((n) => n.id);

      if (userNotifIds.length > 0) {
        await notificationService.markMultipleAsRead(userNotifIds);
      }
      if (adminNotifIds.length > 0) {
        await Promise.all(
          adminNotifIds.map((id) => notificationService.markAdminAsRead(id)),
        );
      }

      toastSuccess(`${selectedNotifications.size} notification(s) updated`);
      setSelectedNotifications(new Set());
      setSelectAll(false);
      fetchAllNotifications();
    } catch (err) {
      toastError("Failed to update notifications");
    }
  };

  const handleSelectAll = () => {
    setSelectAll(!selectAll);
    setSelectedNotifications(
      selectAll ? new Set() : new Set(notifications.map((n) => n.id)),
    );
  };

  const handleSelectNotification = (id) => {
    const newSet = new Set(selectedNotifications);
    newSet.has(id) ? newSet.delete(id) : newSet.add(id);
    setSelectedNotifications(newSet);
    setSelectAll(newSet.size === notifications.length);
  };

  const statCards = [
    {
      icon: Bell,
      label: "Total Notifications",
      value: stats?.total,
      trend: 12,
    },
    { icon: BellRing, label: "Unread", value: stats?.unread },
    { icon: CheckCircle, label: "Read", value: stats?.read },
    { icon: ArchiveIcon, label: "Archived", value: stats?.archived },
    { icon: AlertTriangle, label: "Urgent", value: stats?.urgent },
  ];

  if (loading && !notifications.length) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2
            size={48}
            className="animate-spin text-primary-500 mx-auto mb-4"
          />
          <p className="text-gray-500">Loading notifications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-6 border border-gray-200 dark:border-gray-900 rounded-2xl shadow-lg">
      <div className="space-y-6 max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-400 bg-clip-text text-transparent">
              Notifications
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage and send custom notifications to users
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl flex items-center gap-2 shadow-lg hover:shadow-xl transition-all"
            >
              <PlusCircle size={18} /> Create
            </button>
            <button
              onClick={() => setShowBulkModal(true)}
              className="px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl flex items-center gap-2 shadow-lg hover:shadow-xl transition-all"
            >
              <Users size={18} /> Bulk Send
            </button>
            <button
              onClick={fetchAllNotifications}
              className="px-4 py-2 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm border rounded-xl flex items-center gap-2"
            >
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} />{" "}
              Refresh
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {statCards.map((card, i) => (
            <StatCard
              key={i}
              icon={card.icon}
              label={card.label}
              value={card.value}
              trend={card.trend}
            />
          ))}
        </div>

        <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-gray-800/50 shadow-lg p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 relative">
              <Search
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Search by title, message..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-10 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border focus:ring-2 focus:ring-primary-500"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={16} />
                </button>
              )}
            </div>
            <select
              value={recipientFilter}
              onChange={(e) => setRecipientFilter(e.target.value)}
              className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border"
            >
              <option value="all">All Recipients</option>
              <option value="user">Users Only</option>
              <option value="admin">Admins Only</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border"
            >
              <option value="all">All Status</option>
              <option value="unread">Unread</option>
              <option value="read">Read</option>
              <option value="archived">Archived</option>
            </select>
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border"
            >
              <option value="all">All Priority</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800 rounded-xl border"
            >
              <option value="all">All Types</option>
              <option value="info">Info</option>
              <option value="success">Success</option>
              <option value="warning">Warning</option>
              <option value="error">Error</option>
              <option value="promotion">Promotion</option>
              <option value="order">Order</option>
              <option value="account">Account</option>
              <option value="system">System</option>
            </select>
          </div>
        </div>

        {selectedNotifications.size > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/20 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3"
          >
            <span className="text-sm font-semibold">
              {selectedNotifications.size} selected
            </span>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleBulkRead(true)}
                className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 transition-colors"
              >
                Mark Read
              </button>
              <button
                onClick={handleBulkArchive}
                className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
              >
                Archive
              </button>
              <button
                onClick={handleBulkDelete}
                className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </motion.div>
        )}

        {error && (
          <div className="p-4 bg-red-50 rounded-2xl flex items-center gap-3">
            <AlertCircle className="text-red-500" />
            <p className="text-red-600 text-sm">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto">
              <X size={16} />
            </button>
          </div>
        )}

        <div className="space-y-3">
          <AnimatePresence>
            {notifications.map((notification) => (
              <NotificationCard
                key={notification.id}
                notification={notification}
                isSelected={selectedNotifications.has(notification.id)}
                onSelect={handleSelectNotification}
                onViewDetails={(n) => {
                  setSelectedNotification(n);
                  setShowDetailsModal(true);
                }}
                onMarkAsRead={handleMarkAsRead}
                onArchive={handleArchive}
                onDelete={handleDelete}
              />
            ))}
          </AnimatePresence>
        </div>

        {!notifications.length && !loading && (
          <div className="text-center py-12 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm rounded-2xl border">
            <Bell size={48} className="mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-semibold mb-2">
              No notifications found
            </h3>
            <p className="text-gray-500">
              {searchTerm
                ? "Try adjusting your search"
                : "No notifications to display"}
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 px-4 py-2 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl inline-flex items-center gap-2"
            >
              <PlusCircle size={18} /> Create Your First Notification
            </button>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex justify-center gap-2 p-4">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 border rounded-lg disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Prev
            </button>
            <span className="px-3 py-1.5">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 border rounded-lg disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Next
            </button>
          </div>
        )}
      </div>

      <AnimatePresence>
        {showDetailsModal && selectedNotification && (
          <DetailsModal
            notification={selectedNotification}
            onClose={() => {
              setShowDetailsModal(false);
              setSelectedNotification(null);
            }}
            onMarkAsRead={handleMarkAsRead}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showCreateModal && (
          <CreateNotificationModal
            onClose={() => {
              setShowCreateModal(false);
            }}
            onSuccess={() => {
              fetchAllNotifications();
              setShowCreateModal(false);
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showBulkModal && (
          <BulkSendModal
            onClose={() => {
              setShowBulkModal(false);
            }}
            onSuccess={() => {
              fetchAllNotifications();
              setShowBulkModal(false);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationsManagement;
